import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Pin, PinOff } from "lucide-react";
import { cn } from "../../utils/cn";

/**
 * Menú contextual de UNA fila de la bandeja (como el chevron de WhatsApp Web).
 *
 * Hoy tiene una sola acción (fijar/desfijar), pero se monta como menú para
 * sumar acciones de fila después sin cambiar el disparador ni los manejadores
 * de teclado/click-fuera.
 *
 * @param {object} props
 * @param {boolean} props.pinned - La conversación ya está fijada por el usuario.
 * @param {() => void} props.onTogglePin - Alterna el fijado. El menú se cierra
 *   ANTES de llamarla: el desfijado puede mover la fila de sitio (deja el grupo
 *   "Fijados"), y un menú que sobrevive a ese reacomodo queda anclado a una fila
 *   que ya no está donde estaba.
 * @param {(restoreFocus: boolean) => void} props.onClose - Cierra el menú.
 *   `true` devuelve el foco a quien lo abrió (Escape / acción elegida con el
 *   teclado); `false` lo
 *   deja donde está (clic fuera, Tab), para no robarle el foco al nuevo destino.
 * @param {HTMLElement|null} props.ignoreEl - Elemento cuyo mousedown NO cuenta
 *   como clic fuera (el disparador). Sin esto, el mousedown del disparador
 *   cerraría el menú y el click siguiente lo volvería a abrir.
 * @param {"below"|"above"} [props.placement] - Hacia dónde se abre. Cerca del
 *   borde inferior de la lista, "above" evita que el scroll lo recorte.
 */
export default function ConversationRowMenu({
  pinned,
  onTogglePin,
  onClose,
  ignoreEl,
  placement = "below",
}) {
  const { t } = useTranslation();
  const rootRef = useRef(null);

  // El foco entra al menú al montarse: es lo que permite cerrarlo con Escape y
  // recorrerlo con Tab sin que el usuario tenga que buscarlo. preventScroll
  // porque la fila ya está a la vista y un scroll aquí movería la lista.
  useEffect(() => {
    const first = rootRef.current?.querySelector('[role="menuitem"]');
    first?.focus?.({ preventScroll: true });
  }, []);

  // Clic fuera: mousedown Y touchstart. Se cierra en el DOWN y no en el click
  // para que el menú desaparezca antes de que el gesto toque lo de abajo.
  // touchstart pasivo: este listener solo lee, nunca llama preventDefault.
  useEffect(() => {
    const handlePointerDown = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (ignoreEl?.contains?.(e.target)) return;
      onClose(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [ignoreEl, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      // stopPropagation: el detalle de la conversación ya escucha Escape para
      // sus propios menús/popovers; este Escape es solo del menú de la fila.
      e.preventDefault();
      e.stopPropagation();
      onClose(true);
      return;
    }
    if (e.key === "Tab") {
      // Sin preventDefault: el foco sigue su curso natural (sale del menú) y
      // solo se cierra la capa.
      onClose(false);
    }
  };

  return (
    <div
      ref={rootRef}
      role="menu"
      aria-label={t("Opciones del chat")}
      onKeyDown={handleKeyDown}
      className={cn(
        "absolute right-[10px] z-30 min-w-[184px] py-[4px] bg-bg border border-border rounded-lg shadow-elevated animate-scale-in motion-reduce:animate-none",
        placement === "above"
          ? "bottom-[calc(100%-8px)] origin-bottom-right"
          : "top-[38px] origin-top-right",
      )}
    >
      <button
        type="button"
        role="menuitem"
        onClick={(e) => {
          // El foco vuelve a la fila solo si se eligió con el teclado
          // (detail 0): con mouse o dedo, un anillo de foco sobre la fila que
          // acaba de moverse es ruido, y nadie lo necesita para seguir.
          onClose(e.detail === 0);
          onTogglePin();
        }}
        className="w-full flex items-center gap-[8px] px-[12px] h-[44px] md:h-[32px] text-[13px] text-fg text-left hover:bg-bg-muted focus:outline-none focus-visible:bg-bg-muted"
      >
        {pinned ? (
          <PinOff className="w-3.5 h-3.5 text-fg-muted shrink-0" aria-hidden="true" />
        ) : (
          <Pin className="w-3.5 h-3.5 text-fg-muted shrink-0" aria-hidden="true" />
        )}
        <span className="flex-1">{pinned ? t("Desfijar chat") : t("Fijar chat")}</span>
      </button>
    </div>
  );
}
